import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { unwrapQlResult } from '@core/api/ql-result.util';
import { runtimeConfigFlagEnabled } from 'src/environments/runtime-config';

/** Consumer-facing feature flags the shell reads to hide disabled surfaces. */
export type FeatureFlagKey = 'marketplace' | 'payment' | 'chat' | 'assessment' | 'dailyQuiz';

/**
 * Reads the per-user/org feature flags that decide whether an optional product
 * surface is available (mirrors the legacy `quiz/features/*` checks). The nav
 * hides an entry via the `isEnabled()` signal; the route guard awaits
 * `whenKnown()` so a deep link is redirected only once the backend has confirmed
 * the feature is off. Both share one cached HTTP call. Defaults to enabled, so a
 * failed probe never hides a genuinely-available feature.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private http = inject(HttpClient);

  private readonly enabled = signal<Record<FeatureFlagKey, boolean>>({
    marketplace: true,
    payment: runtimeConfigFlagEnabled('PAYMENT_PUBLIC_API_ENABLED'),
    chat: true,
    assessment: true,
    dailyQuiz: runtimeConfigFlagEnabled('ASSESSMENT_DAILY_CHALLENGES_ENABLED'),
  });

  private readonly chat$: Observable<boolean> = this.http
    .get<unknown>('chat/features/chat')
    .pipe(
      map((res) => {
        const data = unwrapQlResult<{ chatEnabled?: boolean }>(res);
        return data?.chatEnabled !== false;
      }),
      // A failed flag probe must not hide a working feature. The Messages page
      // still fails closed when the backend explicitly reports it disabled.
      catchError(() => of(true)),
      tap((v) => this.enabled.update((f) => ({ ...f, chat: v }))),
      shareReplay(1),
    );

  /** Reactive read used by nav visibility (`canSee`). */
  isEnabled(key: FeatureFlagKey): boolean {
    return this.enabled()[key] ?? true;
  }

  /** Kick off the probe (idempotent — the result is cached). */
  load(): void {
    this.chat$.subscribe();
  }

  /** Resolves once the flag is known; used by the route guard to avoid races. */
  whenKnown(key: FeatureFlagKey): Observable<boolean> {
    if (key === 'chat') return this.chat$;
    return of(this.isEnabled(key));
  }
}
